import { Component, type ReactNode, type ErrorInfo } from 'react';
import posthog from 'posthog-js';
import { SomethingWentWrong } from './SomethingWentWrong';

// Check if we're in development mode
const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary that catches React errors and reports them to PostHog (production only)
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to console for debugging
    console.error('React Error Boundary caught an error:', error, errorInfo);

    // Don't send to PostHog in development
    if (isDevelopment) {
      console.log('[PostHog] Error not sent (development mode)');
      return;
    }

    // Capture error in PostHog (production only)
    try {
      posthog.capture('$exception', {
        $exception_type: error.name,
        $exception_message: error.message,
        $exception_stack_trace: error.stack,
        $exception_source: 'error_boundary',
        component_stack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
      });
    } catch (posthogError) {
      // Don't let PostHog errors break error handling
      console.error('Failed to capture error in PostHog:', posthogError);
    }
  }

  render() {
    if (this.state.hasError) {
      // Render fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return <SomethingWentWrong error={this.state.error} showLayout={false} />;
    }

    return this.props.children;
  }
}
